import { useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useInfiniteQuery, useMutation, useQuery } from 'react-query';
import { accessRequestsApi, resourcesApi, rolesApi, k8sApi } from '../services/api';
import { Resource } from '../types';
import { Search, ChevronRight, ChevronLeft, Clock, Shield, FileText, Check, Key, Crown, KeyRound, TriangleAlert, SearchX, Server } from 'lucide-react';
import { toast } from '@/lib/toast';
import { clsx } from 'clsx';
import { Button, CardButton, Chip, Drawer, EmptyState, Input, ListPageSkeleton, LoadingRegion, NumberInput, RadioGroup, SearchField, Stepper, Switch, Tabs, Textarea } from '@/components/ds-zta';
import { DURATION_PRESETS, MAX_CUSTOM_HOURS, customHoursToSeconds, formatDuration } from '@/features/access-requests/requestModel';
import { toStateTone } from '@/lib/tone';
import { IconTile, ResourceTypeTile } from '@/lib/resourceIcons';

interface AccessRequestFormProps {
  resource?: Resource | null;
  metadata?: Record<string, any>;
  onClose: () => void;
  onSuccess: () => void;
}

interface AccessRequestFormData {
  resource_id: string;
  permissions: string[];
  justification: string;
  duration_seconds: number;
}

type RequestType = 'resource' | 'role';

interface RequestableRole {
  id: string;
  key: string;
  name: string;
  is_admin: boolean;
}

const PERMISSION_OPTIONS: Record<string, string[]> = {
  database: ['read', 'write', 'admin', 'backup'],
  http_proxy: ['get', 'post', 'put', 'delete', 'admin'],
  ssh: ['connect', 'sudo', 'file_transfer'],
  k8s: ['view', 'edit', 'admin', 'exec'],
  desktop: ['connect', 'view', 'clipboard', 'file_transfer'],
  aws_ec2: ['console', 'start_stop', 'admin'],
  aws_s3: ['console', 'read', 'write', 'admin'],
  aws_route53: ['console', 'read', 'write', 'admin'],
  aws_account: ['console', 'read', 'admin'],
  aws_eks: ['console', 'view', 'admin'],
  aws_ecr: ['console', 'read', 'push', 'admin'],
  aws_lambda: ['console', 'invoke', 'admin'],
  aws_rds: ['console', 'read', 'write', 'admin'],
  aws_cloudfront: ['console', 'read', 'admin'],
  aws_ecs: ['console', 'view', 'admin'],
  aws_lightsail: ['console', 'start_stop', 'admin'],
  aws_elasticbeanstalk: ['console', 'view', 'admin'],
  aws_efs: ['console', 'read', 'write', 'admin'],
  aws_dynamodb: ['console', 'read', 'write', 'admin'],
  aws_elasticache: ['console', 'read', 'admin'],
  aws_redshift: ['console', 'read', 'write', 'admin'],
  aws_opensearch: ['console', 'read', 'write', 'admin'],
  aws_vpc: ['console', 'read', 'admin'],
  aws_elb: ['console', 'read', 'admin'],
  aws_apigateway: ['console', 'read', 'write', 'admin'],
  aws_iam: ['console', 'read', 'admin'],
  aws_secretsmanager: ['console', 'read', 'write', 'admin'],
  aws_kms: ['console', 'read', 'admin'],
  aws_sqs: ['console', 'read', 'write', 'admin'],
  aws_sns: ['console', 'read', 'write', 'admin'],
  aws_cloudwatch: ['console', 'read', 'admin'],
  azure_vm: ['portal', 'start_stop', 'admin'],
  azure_storage: ['portal', 'read', 'write', 'admin'],
  azure_acr: ['portal', 'read', 'push', 'admin'],
  hetzner_server: ['console', 'start_stop', 'admin'],
  hetzner_volume: ['console', 'read', 'write', 'admin'],
  hetzner_loadbalancer: ['console', 'read', 'admin'],
};

/** The duration radio group: the presets, then a custom number of hours. */
const DURATION_OPTIONS = [
  ...DURATION_PRESETS.map((p) => ({ value: String(p.seconds), label: p.label })),
  { value: 'custom', label: 'Custom', description: `Whole hours, up to ${MAX_CUSTOM_HOURS}` },
];

/** The resource picker loads requestable resources this many at a time. */
const RESOURCE_PAGE_SIZE = 100;

const RESOURCE_STEPS = [
  { key: 'resource', label: 'Resource', icon: Search },
  { key: 'permissions', label: 'Permissions', icon: Shield },
  { key: 'duration', label: 'Duration', icon: Clock },
  { key: 'justification', label: 'Justification', icon: FileText },
];

const ROLE_STEPS = [
  { key: 'role', label: 'Role', icon: Key },
  { key: 'duration', label: 'Duration', icon: Clock },
  { key: 'justification', label: 'Justification', icon: FileText },
];

export default function AccessRequestForm({ resource, metadata: externalMetadata, onClose, onSuccess }: AccessRequestFormProps) {
  const [requestType, setRequestType] = useState<RequestType>(resource ? 'resource' : 'resource');
  const [step, setStep] = useState(resource ? 1 : 0);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(resource || null);
  const [selectedRole, setSelectedRole] = useState<RequestableRole | null>(null);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  // The duration choice is a preset's seconds or 'custom'; selectedDuration is
  // what gets sent, 0 while a custom value is not valid (blocks Next).
  const [durationChoice, setDurationChoice] = useState<string>(String(4 * 60 * 60));
  const [customHours, setCustomHours] = useState<number | null>(12);
  const selectedDuration = durationChoice === 'custom'
    ? (customHoursToSeconds(customHours) ?? 0)
    : Number(durationChoice);
  const [resourceSearch, setResourceSearch] = useState('');
  // K8s namespace scoping (only used when the selected resource is a k8s cluster).
  const [allNamespaces, setAllNamespaces] = useState(false);
  const [selectedNamespaces, setSelectedNamespaces] = useState<string[]>([]);
  const [namespaceInput, setNamespaceInput] = useState('');

  const isK8s = selectedResource?.type === 'k8s';

  // Fetch the cluster's namespaces so the user can scope the request. Falls back
  // to manual entry if the endpoint/agent is unavailable.
  const { data: namespacesData, isLoading: namespacesLoading, isError: namespacesError } = useQuery(
    ['k8s-namespaces', selectedResource?.external_id],
    () => k8sApi.listNamespaces(selectedResource!.external_id),
    { enabled: !!isK8s && !!selectedResource?.external_id, retry: false }
  );
  const clusterNamespaces = namespacesData?.data?.namespaces ?? [];

  const toggleNamespace = (ns: string) =>
    setSelectedNamespaces((prev) => (prev.includes(ns) ? prev.filter((n) => n !== ns) : [...prev, ns]));
  const addManualNamespace = () => {
    const ns = namespaceInput.trim();
    if (ns && !selectedNamespaces.includes(ns)) setSelectedNamespaces((prev) => [...prev, ns]);
    setNamespaceInput('');
  };

  const STEPS = requestType === 'role' ? ROLE_STEPS : RESOURCE_STEPS;

  const { register, handleSubmit, formState: { errors } } = useForm<AccessRequestFormData>();

  // Design eval F9: this stopped at the first 100 requestable resources and
  // said nothing. /resources/requestable pages (limit/offset, no search), so
  // the picker loads 100 at a time and offers the rest with "Load more"; the
  // search narrows what is loaded and says so while more remain.
  const {
    data: resourcesPages,
    isLoading: resourcesLoading,
    isError: resourcesError,
    refetch: refetchResources,
    fetchNextPage: fetchMoreResources,
    hasNextPage: hasMoreResources,
    isFetchingNextPage: fetchingMoreResources,
  } = useInfiniteQuery(
    'resources-for-form',
    ({ pageParam = 0 }) => resourcesApi.listRequestable(undefined, RESOURCE_PAGE_SIZE, pageParam as number),
    {
      enabled: !resource && requestType === 'resource',
      getNextPageParam: (last, pages) => {
        const loaded = pages.reduce((n, p) => n + (p.data.resources?.length ?? 0), 0);
        const got = last.data.resources?.length ?? 0;
        return got > 0 && loaded < (last.data.total ?? 0) ? loaded : undefined;
      },
    }
  );
  const loadedResources: Resource[] = resourcesPages?.pages.flatMap((p) => p.data.resources ?? []) ?? [];
  const resourcesTotal = Math.max(resourcesPages?.pages[0]?.data.total ?? 0, loadedResources.length);

  const { data: rolesData, isLoading: rolesLoading, isError: rolesError, refetch: refetchRoles } = useQuery('requestable-roles', () =>
    rolesApi.listRequestable(),
    { enabled: requestType === 'role' }
  );

  const createMutation = useMutation(accessRequestsApi.create, {
    onSuccess: () => {
      onSuccess();
      toast.success('Access request created successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to create access request');
    }
  });

  const availablePermissions = selectedResource
    ? PERMISSION_OPTIONS[selectedResource.type] || []
    : [];

  const filteredResources = loadedResources.filter((r: Resource) =>
    r.external_id.toLowerCase().includes(resourceSearch.toLowerCase()) ||
    r.type.toLowerCase().includes(resourceSearch.toLowerCase())
  );

  const filteredRoles = rolesData?.data.roles?.filter((r: RequestableRole) =>
    r.name.toLowerCase().includes(resourceSearch.toLowerCase()) ||
    r.key.toLowerCase().includes(resourceSearch.toLowerCase())
  ) || [];

  const togglePermission = (perm: string) => {
    setSelectedPermissions(prev =>
      prev.includes(perm) ? prev.filter(p => p !== perm) : [...prev, perm]
    );
  };

  const switchRequestType = (type: RequestType) => {
    setRequestType(type);
    setStep(0);
    setSelectedResource(null);
    setSelectedRole(null);
    setSelectedPermissions([]);
    setResourceSearch('');
  };

  const canProceed = (): boolean => {
    if (requestType === 'role') {
      switch (step) {
        case 0: return !!selectedRole;
        case 1: return selectedDuration > 0;
        case 2: return true;
        default: return false;
      }
    }
    switch (step) {
      case 0: return !!selectedResource;
      case 1:
        if (selectedPermissions.length === 0) return false;
        // For k8s, require an explicit namespace scope (specific namespaces or "all").
        if (isK8s && !allNamespaces && selectedNamespaces.length === 0) return false;
        return true;
      case 2: return selectedDuration > 0;
      case 3: return true;
      default: return false;
    }
  };

  const onSubmit = async (data: AccessRequestFormData) => {
    if (requestType === 'role') {
      if (!selectedRole) return;
      await createMutation.mutateAsync({
        request_type: 'role',
        role_id: selectedRole.id,
        resource_type: 'role',
        permissions: ['full_access'],
        justification: data.justification,
        duration_seconds: selectedDuration,
      } as any);
    } else {
      if (!selectedResource) return;
      // Scope k8s requests by namespace: ['*'] means all namespaces (cluster-wide),
      // otherwise the explicitly selected namespaces. Rides in metadata.namespaces,
      // which the k8s-proxy and k8s-agent read to enforce the grant.
      const k8sMetadata = isK8s
        ? { namespaces: allNamespaces ? ['*'] : selectedNamespaces }
        : {};
      const metadata = { ...(externalMetadata || {}), ...k8sMetadata };
      await createMutation.mutateAsync({
        request_type: 'resource',
        resource_id: selectedResource.id,
        resource_type: selectedResource.type,
        permissions: selectedPermissions,
        justification: data.justification,
        duration_seconds: selectedDuration,
        ...(Object.keys(metadata).length ? { metadata } : {}),
      } as any);
    }
  };

  // Determine which step content to show for role vs resource flows
  const renderStepContent = () => {
    if (requestType === 'role') {
      if (step === 0) return renderRoleSelection();
      if (step === 1) return renderDuration();
      if (step === 2) return renderJustification();
    } else {
      if (step === 0) return renderResourceSelection();
      if (step === 1) return renderPermissions();
      if (step === 2) return renderDuration();
      if (step === 3) return renderJustification();
    }
    return null;
  };

  const renderRoleSelection = () => (
    <div className="space-y-3">
      <SearchField
        label="Search roles"
        placeholder="Search roles..."
        value={resourceSearch}
        onChange={(value) => setResourceSearch(value)}
      />
      <div className="space-y-1.5 max-h-[calc(100vh-380px)] overflow-y-auto">
        {filteredRoles.map((role: RequestableRole) => (
          <CardButton
            key={role.id}
            aria-pressed={selectedRole?.id === role.id}
            onClick={() => setSelectedRole(role)}
            className={clsx(
              'w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left',
              selectedRole?.id === role.id
                ? 'border-[var(--brand)] bg-[var(--ds-color-bg-subtle)]'
                : 'border-[var(--d2d-border)] hover:border-[var(--ds-color-border-strong)] hover:bg-[var(--ds-color-bg-hover)]'
            )}
          >
            <IconTile icon={role.is_admin ? Crown : KeyRound} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[var(--d2d-text-primary)] truncate">
                {role.name}
              </p>
              <p className="text-xs text-[var(--d2d-text-muted)]">{role.key}</p>
            </div>
            {role.is_admin && (
              <Chip variant="neutral">ADMIN</Chip>
            )}
            {selectedRole?.id === role.id && (
              <Check className="h-4 w-4 text-[var(--d2d-text-primary)] flex-shrink-0" />
            )}
          </CardButton>
        ))}
        {rolesLoading ? (
          <ListPageSkeleton aria-label="Loading roles" filterCount={0} rowCount={6} />
        ) : rolesError ? (
          <EmptyState
            icon={<TriangleAlert />}
            title="Could not load roles"
            action={<Button variant="secondary" size="sm" onClick={() => refetchRoles()}>Retry</Button>}
          />
        ) : filteredRoles.length === 0 && (
          resourceSearch ? (
            <EmptyState icon={<SearchX />} title={`No roles match "${resourceSearch}"`} />
          ) : (
            <EmptyState icon={<KeyRound />} title="No roles found" description="There are no roles you can request." />
          )
        )}
      </div>
    </div>
  );

  const renderResourceSelection = () => (
    <div className="space-y-3">
      <SearchField
        label="Search resources"
        placeholder="Search resources..."
        value={resourceSearch}
        onChange={(value) => setResourceSearch(value)}
        help={
          hasMoreResources
            ? `Searches the ${loadedResources.length} of ${resourcesTotal} resources loaded. Load more to search the rest.`
            : undefined
        }
      />
      <div className="space-y-1.5 max-h-[calc(100vh-380px)] overflow-y-auto">
        {filteredResources.map((res: Resource) => (
          <CardButton
            key={res.id}
            aria-pressed={selectedResource?.id === res.id}
            onClick={() => {
              setSelectedResource(res);
              setSelectedPermissions([]);
            }}
            className={clsx(
              'w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left',
              selectedResource?.id === res.id
                ? 'border-[var(--brand)] bg-[var(--ds-color-bg-subtle)]'
                : 'border-[var(--d2d-border)] hover:border-[var(--ds-color-border-strong)] hover:bg-[var(--ds-color-bg-hover)]'
            )}
          >
            <ResourceTypeTile type={res.type} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[var(--d2d-text-primary)] truncate font-mono tabular-nums">
                {res.external_id}
              </p>
              <p className="text-xs text-[var(--d2d-text-muted)] capitalize">{res.type}</p>
            </div>
            {/* Health, drawn in the shared vocabulary's tone (src/lib/tone.ts). */}
            <span
              data-tone={toStateTone(res.health_status || 'unknown')}
              className="h-2 w-2 rounded-full bg-[var(--tone)] flex-shrink-0"
              aria-hidden="true"
            />
            {selectedResource?.id === res.id && (
              <Check className="h-4 w-4 text-[var(--d2d-text-primary)] flex-shrink-0" />
            )}
          </CardButton>
        ))}
        {resourcesLoading ? (
          <ListPageSkeleton aria-label="Loading resources" filterCount={0} rowCount={6} />
        ) : resourcesError ? (
          <EmptyState
            icon={<TriangleAlert />}
            title="Could not load resources"
            action={<Button variant="secondary" size="sm" onClick={() => refetchResources()}>Retry</Button>}
          />
        ) : filteredResources.length === 0 && (
          resourceSearch ? (
            <EmptyState icon={<SearchX />} title={`No resources match "${resourceSearch}"`} />
          ) : (
            <EmptyState icon={<Server />} title="No resources found" description="There are no resources you can request." />
          )
        )}
        {hasMoreResources && !resourcesError && (
          <div className="flex items-center justify-between gap-3 pt-2">
            <span className="text-xs text-[var(--d2d-text-muted)]">
              Showing {loadedResources.length} of {resourcesTotal}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => fetchMoreResources()}
              loading={fetchingMoreResources}
            >
              Load more
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  const renderPermissions = () => (
    <div className="space-y-3">
      <p className="text-xs text-[var(--d2d-text-muted)]">
        Select the permissions you need for{' '}
        <span className="font-medium text-[var(--d2d-text-secondary)] font-mono tabular-nums">
          {selectedResource?.external_id}
        </span>
      </p>
      <div className="grid grid-cols-2 gap-2">
        {availablePermissions.map((perm) => (
          <CardButton
            key={perm}
            aria-pressed={selectedPermissions.includes(perm)}
            onClick={() => togglePermission(perm)}
            className={clsx(
              'flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium border transition-all capitalize',
              selectedPermissions.includes(perm)
                ? 'border-[var(--brand)] bg-[var(--ds-color-bg-subtle)] text-[var(--d2d-text-primary)]'
                : 'border-[var(--d2d-border)] text-[var(--d2d-text-secondary)] hover:border-[var(--ds-color-border-strong)] hover:bg-[var(--ds-color-bg-hover)]'
            )}
          >
            {selectedPermissions.includes(perm) && <Check className="h-4 w-4" />}
            {perm}
          </CardButton>
        ))}
      </div>
      {selectedPermissions.length === 0 && (
        <p className="text-xs text-[var(--danger-text)] mt-1">At least one permission is required</p>
      )}

      {isK8s && (
        <div className="pt-3 mt-1 border-t border-[var(--d2d-border)] space-y-2">
          <p className="text-xs text-[var(--d2d-text-muted)]">
            Scope these permissions to specific <span className="font-medium">namespaces</span>. Access outside the
            selected namespaces is denied in both the web terminal and the CLI.
          </p>

          {/* All-namespaces toggle */}
          <Switch
            label="All namespaces (cluster-wide)"
            checked={allNamespaces}
            onChange={(checked) => setAllNamespaces(checked)}
          />

          {!allNamespaces && (
            <>
              {namespacesLoading && (
                <LoadingRegion lines={2} label="Loading namespaces" />
              )}
              {clusterNamespaces.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {clusterNamespaces.map((ns) => (
                    <Chip
                      key={ns}
                      onClick={() => toggleNamespace(ns)}
                      pressed={selectedNamespaces.includes(ns)}
                    >
                      {selectedNamespaces.includes(ns) && <Check className="h-3.5 w-3.5" aria-hidden="true" />}
                      {ns}
                    </Chip>
                  ))}
                </div>
              )}

              {(namespacesError || clusterNamespaces.length === 0) && !namespacesLoading && (
                <p className="text-xs text-[var(--d2d-text-muted)]">
                  Couldn't list namespaces automatically — add them by name below.
                </p>
              )}

              {/* Manual add (works even when live listing is unavailable) */}
              <div className="flex gap-2">
                <Input
                  label="Add a namespace"
                  hideLabel
                  size="sm"
                  className="flex-1"
                  type="text"
                  value={namespaceInput}
                  onChange={(e) => setNamespaceInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addManualNamespace();
                    }
                  }}
                  placeholder="Add a namespace…"
                />
                <Button variant="secondary" size="sm" type="button" onClick={addManualNamespace}>
                  Add
                </Button>
              </div>

              {/* Selected chips */}
              {selectedNamespaces.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {selectedNamespaces.map((ns) => (
                    <Chip
                      key={ns}
                      onRemove={() => toggleNamespace(ns)}
                      removeLabel={`Remove ${ns}`}
                    >
                      {ns}
                    </Chip>
                  ))}
                </div>
              )}

              {selectedNamespaces.length === 0 && (
                <p className="text-xs text-[var(--danger-text)]">Select at least one namespace, or choose “All namespaces”.</p>
              )}
            </>
          )}

          {selectedPermissions.includes('exec') && (
            <p className="flex items-start gap-1.5 text-xs text-[var(--d2d-warning)]">
              <TriangleAlert className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
              <span>The <span className="font-medium">exec</span> permission allows running commands inside pods
              (<code>kubectl exec</code>) in the granted namespaces.</span>
            </p>
          )}
        </div>
      )}
    </div>
  );

  const renderDuration = () => (
    <div className="space-y-3">
      <p className="text-xs text-[var(--d2d-text-muted)]">
        {requestType === 'role'
          ? 'Role access will be automatically revoked after the selected duration.'
          : 'Access will be automatically revoked after the selected duration.'}
      </p>
      <RadioGroup
        legend="Access duration"
        hideLegend
        name="access-duration"
        options={DURATION_OPTIONS}
        value={durationChoice}
        onChange={setDurationChoice}
      />
      {durationChoice === 'custom' && (
        <NumberInput
          label="Custom duration"
          suffix="hours"
          min={1}
          max={MAX_CUSTOM_HOURS}
          step={1}
          precision={0}
          value={customHours}
          onChange={setCustomHours}
          help={`From 1 to ${MAX_CUSTOM_HOURS} hours. A policy may set a lower limit, which the server enforces.`}
          error={customHoursToSeconds(customHours) === null ? `Enter whole hours from 1 to ${MAX_CUSTOM_HOURS}` : undefined}
        />
      )}
    </div>
  );

  const renderJustification = () => (
    <div className="space-y-3">
      <p className="text-xs text-[var(--d2d-text-muted)]">
        Provide a clear explanation for why you need access.
      </p>
      <Textarea
        label={<span className="sr-only">Justification</span>}
        {...register('justification', {
          required: 'Justification is required',
          minLength: { value: 10, message: 'At least 10 characters' },
        })}
        rows={5}
        placeholder={requestType === 'role'
          ? 'I need temporary admin access to configure the new deployment pipeline…'
          : 'I need access to deploy a bug fix for ticket JIRA-1234…'}
      />
      {errors.justification && (
        <p className="text-xs text-[var(--danger-text)]">{errors.justification.message}</p>
      )}

      {/* Summary */}
      <div className="rounded-xl bg-[var(--d2d-bg-secondary)] p-4 space-y-2 mt-4">
        <h4 className="text-label font-semibold text-[var(--d2d-text-muted)] uppercase">Summary</h4>
        <div className="text-sm text-[var(--d2d-text-secondary)] space-y-1">
          {requestType === 'role' ? (
            <p>
              <span className="text-[var(--d2d-text-muted)]">Role:</span>{' '}
              {selectedRole?.name} {selectedRole?.is_admin && <span className="text-[var(--d2d-warning)]">(Admin)</span>}
            </p>
          ) : (
            <>
              <p>
                <span className="text-[var(--d2d-text-muted)]">Resource:</span>{' '}
                {selectedResource?.external_id}
              </p>
              <p>
                <span className="text-[var(--d2d-text-muted)]">Permissions:</span>{' '}
                {selectedPermissions.join(', ')}
              </p>
            </>
          )}
          <p>
            <span className="text-[var(--d2d-text-muted)]">Duration:</span>{' '}
            {formatDuration(selectedDuration)}
          </p>
          <p>
            <span className="text-[var(--d2d-text-muted)]">Type:</span>{' '}
            <span className="capitalize">{requestType} access request</span>
          </p>
        </div>
      </div>
    </div>
  );

  const formId = useId();
  const isLastStep = step === STEPS.length - 1;
  const isSubmitDisabled = requestType === 'role'
    ? createMutation.isLoading
    : createMutation.isLoading || selectedPermissions.length === 0;

  return (
    <Drawer
      open
      onClose={onClose}
      side="end"
      size="lg"
      dismissOnOverlay
      title="Request Access"
      description={`Step ${step + 1} of ${STEPS.length}`}
      footer={
        <div className="flex w-full items-center gap-3">
          {step > 0 && (
            <Button variant="secondary" type="button" onClick={() => setStep(step - 1)}>
              <ChevronLeft className="h-4 w-4" />
              Back
            </Button>
          )}
          <div className="flex-1" />
          {!isLastStep ? (
            <Button type="button" onClick={() => setStep(step + 1)} disabled={!canProceed()}>
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button type="submit" form={formId} disabled={isSubmitDisabled} loading={createMutation.isLoading}>
              {createMutation.isLoading ? 'Submitting…' : 'Submit Request'}
            </Button>
          )}
        </div>
      }
    >
        {/* Request Type Toggle — only show at step 0 when no pre-selected resource */}
        {step === 0 && !resource && (
          <div className="mb-4">
            <Tabs
              variant="segmented"
              value={requestType}
              onChange={(v) => switchRequestType(v as RequestType)}
              aria-label="Request type"
            >
              <Tabs.List>
                <Tabs.Tab value="resource">
                  <span className="flex items-center justify-center gap-2">
                    <Search className="h-4 w-4" aria-hidden="true" />
                    Resource
                  </span>
                </Tabs.Tab>
                <Tabs.Tab value="role">
                  <span className="flex items-center justify-center gap-2">
                    <Key className="h-4 w-4" aria-hidden="true" />
                    Role
                  </span>
                </Tabs.Tab>
              </Tabs.List>
            </Tabs>
          </div>
        )}

        {/* Progress Bar */}
        <div className="mb-4">
          <Stepper
            aria-label="Request steps"
            current={step}
            steps={STEPS.map((st) => ({ title: st.label }))}
          />
        </div>

        {/* Content */}
        <form id={formId} onSubmit={handleSubmit(onSubmit)}>
          {renderStepContent()}
        </form>
    </Drawer>
  );
}
